import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { collaboratorSalaryService } from '../../Services/collaboratorSalaryService';
import { hasSalaryPermission } from '../../Models/collaboratorSalary.types';
import { toast } from 'react-hot-toast';

interface SalaryUpdateData {
  amount: number;
  extras: number;
  salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
  effectiveDate: string;
}

/**
 * Hook for salary-related mutations with optimistic updates and error handling
 */
export const useSalaryMutation = (collaboratorId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userRoles = user?.roles || [];
  const collaboratorIdSafe = collaboratorId?.trim();

  type SalaryHistoryUpdate = {
    historyId: string;
    data: {
      amount: number;
      extras: number;
      salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
      changeDate: string;
      changeReason: string;
      changeObservations?: string;
      recordedAt?: Date;
      recordedBy?: string;
    };
  };

  // Update salary with optimistic update
  const updateSalaryMutation = useMutation({
    mutationFn: async ({
      data,
      reason,
      observations,
    }: {
      data: SalaryUpdateData;
      reason: string;
      observations?: string;
    }) => {
      // Check permissions
      if (!hasSalaryPermission(userRoles, 'canEditSalary')) {
        throw new Error('Você não tem permissão para editar salários');
      }

      if (!collaboratorIdSafe) {
        throw new Error('Colaborador inválido para editar salário');
      }

      return collaboratorSalaryService.updateSalary(
        collaboratorIdSafe,
        data,
        reason,
        observations,
        user?.uid
      );
    },
    onError: (error) => {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Erro ao atualizar salário';
      toast.error(errorMessage);
    },
    onSuccess: () => {
      // Show success message
      toast.success('Salário atualizado com sucesso!');

      // Invalidate queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalary', collaboratorId]
      });
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalaryHistory', collaboratorId]
      });
    },
  });

  // Update existing history entry
  const updateHistoryMutation = useMutation({
    mutationFn: async ({ historyId, data }: SalaryHistoryUpdate) => {
      if (!hasSalaryPermission(userRoles, 'canEditSalary')) {
        throw new Error('Você não tem permissão para editar salários');
      }

      if (!collaboratorIdSafe) {
        throw new Error('Colaborador inválido para editar salário');
      }

      return collaboratorSalaryService.updateSalaryHistoryEntry(
        collaboratorIdSafe,
        historyId,
        {
          amount: data.amount,
          extras: data.extras,
          salaryType: data.salaryType,
          changeDate: data.changeDate,
          changeReason: data.changeReason,
          changeObservations: data.changeObservations,
        },
        user?.uid
      );
    },
    onSuccess: () => {
      toast.success('Registro salarial atualizado!');
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalary', collaboratorId]
      });
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalaryHistory', collaboratorId]
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Erro ao atualizar registro salarial';
      toast.error(errorMessage);
    },
  });

  // Delete history entry with confirmation
  const deleteHistoryMutation = useMutation({
    mutationFn: async (historyId: string) => {
      // Check permissions
      if (!hasSalaryPermission(userRoles, 'canDeleteSalary')) {
        throw new Error('Apenas administradores podem excluir histórico de salário');
      }

      if (!collaboratorIdSafe) {
        throw new Error('Colaborador inválido para editar salário');
      }

      return collaboratorSalaryService.deleteSalaryHistory(collaboratorIdSafe, historyId, userRoles);
    },
    onMutate: async (historyId) => {
      // Cancel in-flight queries
      await queryClient.cancelQueries({
        queryKey: ['collaboratorSalaryHistory', collaboratorId]
      });

      // Snapshot previous value
      const previousHistory = queryClient.getQueryData([
        'collaboratorSalaryHistory',
        collaboratorId
      ]);

      // Optimistically remove from list
      queryClient.setQueryData(
        ['collaboratorSalaryHistory', collaboratorId],
        (old: any[]) => old?.filter((item: any) => item.id !== historyId) || []
      );

      return { previousHistory };
    },
    onError: (error, _, context) => {
      // Rollback on error
      if (context?.previousHistory) {
        queryClient.setQueryData(
          ['collaboratorSalaryHistory', collaboratorId],
          context.previousHistory
        );
      }

      // Show error message
      const errorMessage = error instanceof Error
        ? error.message
        : 'Erro ao excluir histórico';
      toast.error(errorMessage);
    },
    onSuccess: () => {
      // Show success message
      toast.success('Histórico excluído com sucesso!');

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalaryHistory', collaboratorId]
      });
    },
  });

  // Batch update multiple salaries
  const batchUpdateMutation = useMutation({
    mutationFn: async (updates: Array<{
      collaboratorId: string;
      data: SalaryUpdateData;
      reason: string;
      observations?: string;
    }>) => {
      // Check permissions
      if (!hasSalaryPermission(userRoles, 'canEditSalary')) {
        throw new Error('Você não tem permissão para editar salários');
      }

      // Process updates in parallel
      const promises = updates.map(update =>
        collaboratorSalaryService.updateSalary(
          update.collaboratorId,
          update.data,
          update.reason,
          update.observations,
          user?.uid
        )
      );

      return Promise.all(promises);
    },
    onSuccess: () => {
      toast.success('Salários atualizados com sucesso!');

      // Invalidate all salary queries
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalary']
      });
      queryClient.invalidateQueries({
        queryKey: ['collaboratorSalaryHistory']
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Erro ao atualizar salários';
      toast.error(errorMessage);
    },
  });

  return {
    updateSalary: updateSalaryMutation,
    updateHistory: updateHistoryMutation,
    deleteHistory: deleteHistoryMutation,
    batchUpdate: batchUpdateMutation,
    canEdit: hasSalaryPermission(userRoles, 'canEditSalary'),
    canDelete: hasSalaryPermission(userRoles, 'canDeleteSalary'),
  };
};
